import React from 'react';
import { Alert, App, Input, Space, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { warningCopy } from './operationalExceptionText';
import type { OperationalWarning } from './operationalExceptionText';

type ModalApi = ReturnType<typeof App.useApp>['modal'];

export type { OperationalWarning } from './operationalExceptionText';
// Re-exported so existing callers keep one import; the copy itself lives in
// operationalExceptionText.ts (single home, shared with the Rozbieżności page).
export { warningCopy, warningText } from './operationalExceptionText';

function readConfirmation(error: any) {
  const data = error?.response?.data;
  if (data?.code !== 'OPERATIONAL_EXCEPTION_CONFIRMATION_REQUIRED') return null;
  if (!data.confirmationFingerprint || !Array.isArray(data.warnings)) return null;
  return data as {
    confirmationFingerprint: string;
    warnings: OperationalWarning[];
    reasonMinLength?: number;
    reasonMaxLength?: number;
  };
}

/**
 * One warning = one short Polish sentence. Several warnings read as a bulleted
 * list so the operator can scan them. A code the frontend does not know still
 * gets a sentence; its raw code appears only as the small grey support caption.
 */
const WarningList: React.FC<{ warnings: OperationalWarning[]; t: TFunction }> = ({ warnings, t }) => {
  const items = warnings.map((warning, index) => ({
    key: `${warning.code}-${index}`,
    ...warningCopy(warning, t),
  }));
  const body = (item: typeof items[number]) => (
    <>
      <span style={{ whiteSpace: 'pre-line' }}>{item.text}</span>
      {item.supportCode && (
        <>
          {' '}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('common:operationalException.supportCode', { code: item.supportCode })}
          </Typography.Text>
        </>
      )}
    </>
  );
  if (items.length === 1) {
    return <Typography.Paragraph style={{ marginBottom: 0 }}>{body(items[0])}</Typography.Paragraph>;
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {items.map((item) => (
        <li key={item.key} style={{ marginBottom: 4 }}>{body(item)}</li>
      ))}
    </ul>
  );
};

async function askForReason(
  modal: ModalApi,
  warnings: OperationalWarning[],
  maxLength: number,
  t: TFunction,
  minLength = 1,
): Promise<string | null> {
  let reason = '';
  return new Promise((resolve) => {
    modal.confirm({
      title: t('common:operationalException.title'),
      width: 620,
      okText: t('common:operationalException.continue'),
      cancelText: t('common:cancel'),
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('common:operationalException.description')}
          />
          <WarningList warnings={warnings} t={t} />
          <div>
            <Typography.Text strong>{t('common:operationalException.reasonLabel')}</Typography.Text>
            <Input.TextArea
              rows={3}
              maxLength={maxLength}
              showCount
              placeholder={t('common:operationalException.reasonPlaceholder')}
              onChange={(event) => { reason = event.target.value; }}
            />
            {minLength > 1 && (
              <Typography.Text type="secondary">
                {t('common:operationalException.reasonMinLength', { min: minLength })}
              </Typography.Text>
            )}
          </div>
        </Space>
      ),
      onOk: () => {
        if (!reason.trim()) return Promise.reject(new Error(t('common:operationalException.reasonRequired')));
        if (reason.trim().length < minLength) {
          return Promise.reject(new Error(t('common:operationalException.reasonMinLength', { min: minLength })));
        }
        resolve(reason.trim());
        return Promise.resolve();
      },
      onCancel: () => resolve(null),
    });
  });
}

export async function submitWithOperationalException<T>(
  request: (payload: Record<string, unknown>) => Promise<T>,
  payload: Record<string, unknown>,
  modal: ModalApi,
  t: TFunction,
): Promise<T | null> {
  let nextPayload = payload;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await request(nextPayload);
    } catch (error) {
      const confirmation = readConfirmation(error);
      if (!confirmation) throw error;
      const reason = await askForReason(
        modal,
        confirmation.warnings,
        confirmation.reasonMaxLength || 500,
        t,
        confirmation.reasonMinLength || 1,
      );
      if (!reason) return null;
      nextPayload = {
        ...payload,
        confirmOperationalException: true,
        operationalExceptionFingerprint: confirmation.confirmationFingerprint,
        operationalExceptionReason: reason,
      };
    }
  }
  throw new Error('Operational exception confirmation changed repeatedly');
}
